import { Link } from 'react-router-dom'
import type { EngagedTopic } from '../lib/api'
import { chatTarget } from '../lib/chatNavigation'
import { outboxTopicKey, useOutboxPendingScopes } from '../lib/outboxPending'
import { relativeTimeFromNow } from '../lib/time'
import { engagedTopicState } from '../lib/engagedTopicFilters'
import AgentBadge from './AgentBadge'
import EngagedTopicDoneButton from './EngagedTopicDoneButton'

export default function EngagedTopicsView({ topics, loading, error, onRetry }: {
  topics: EngagedTopic[]
  loading: boolean
  error: boolean
  onRetry: () => void
}) {
  const { topicKeys } = useOutboxPendingScopes()
  return (
    <section aria-label="Topics I've replied to">
      <div className="px-3 py-2 text-xs font-medium text-stone-500 border-y border-stone-200 dark:border-stone-800">
        Topics I've replied to · {topics.length}
      </div>
      {error && <div role="status" className="px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
        Could not refresh topics. <button type="button" onClick={onRetry} className="underline min-h-11">Retry</button>
      </div>}
      {loading && <p className="p-3 text-sm text-stone-500">Loading topics…</p>}
      {!loading && !error && topics.length === 0 && <p className="p-3 text-sm text-stone-500">No topics match this filter.</p>}
      {topics.map((topic) => (
        <div
          key={topic.id}
          data-engaged-topic-id={topic.id}
          className="flex items-center border-b border-stone-100 dark:border-stone-800/60"
        >
          <Link
            to={chatTarget(topic.chat_id, topic.id, { parentTab: 'lanes' })}
            className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3 hover:bg-stone-100 dark:hover:bg-stone-800/60"
          >
            <AgentBadge agent={topic.agent} size={30} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{topic.title || 'Untitled topic'}</div>
              {topic.pending_approval && <div className="text-xs font-semibold text-amber-700 dark:text-amber-300">Approval needed</div>}
              <div className="truncate text-xs text-stone-500">{topic.chat_display_name || topic.chat_id} · {relativeTimeFromNow(topic.last_activity_at)}</div>
            </div>
            <span className="text-xs text-stone-500">{engagedTopicState(topic) === 'snooze' ? 'Snoozed'
              : topic.working ? 'Working'
              : topic.queued || topicKeys.has(outboxTopicKey(topic.chat_id, topic.id)) ? 'Queued'
              : topic.scheduled_recheck_at != null ? 'Waiting' : 'Idle'}</span>
            {topic.unread > 0 && <span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-xs text-white" aria-label={`${topic.unread} unread messages`}>{topic.unread}</span>}
          </Link>
          <EngagedTopicDoneButton topic={topic} />
        </div>
      ))}
    </section>
  )
}
